import type { LucideIcon } from "lucide-react";
import { Images, RefreshCw, Sparkles } from "lucide-react";

// Sales-led product: the main conversion is the lead form dialog.
export const LEAD_FORM_ANCHOR = "#empezar";

export type LeadFormLocation =
  | "header"
  | "hero"
  | "product_flow"
  | "product_ui"
  | "testimonials"
  | "pricing"
  | "sticky_mobile"
  | "final";

export const HERO = {
  eyebrow: "Para marcas de moda",
  subtitle:
    "Trendo Boost gestiona y optimiza tus campañas para ayudarte a vender más, utilizando datos reales de búsqueda de Trendo.",
  formTitle: "Empezá hoy",
  formSubtitle: "Dejanos tus datos y te contactamos por WhatsApp.",
  socialProof: "Amalfi, Handbag y Almira ya trabajan con Trendo Boost.",
};

/**
 * Objections answered before the visitor has to scroll: the platform question
 * is the most common one, since we nos integramos con Meta, no con la tienda.
 */
export const HERO_TRUST_POINTS: string[] = [
  "Funciona con cualquier tienda online",
  "Configuración en menos de 15 minutos",
  "Te escribimos por WhatsApp",
];

export const FINAL_CTA = {
  subtitle: "Contanos sobre tu marca y te escribimos para empezar.",
  heroCta: "Quiero empezar",
  pricingCta: "Quiero empezar",
  midPageCta: "Quiero empezar",
  headerCta: "Quiero empezar",
  stickyCta: "Quiero empezar",
};

export const LEAD_FORM = {
  submitCta: "Quiero que me contacten",
  submittingCta: "Revisando tu tienda...",
  reassurance:
    "Sin compromiso. Te contactamos por WhatsApp para contarte cómo funciona.",
  detailsTitle: "Un poco más para conocerte",
  detailsIntro: "Esto nos ayuda a llegar preparados a la conversación.",
  doneTitle: "¡Gracias!",
  doneSubtitle: "Te contactaremos por WhatsApp en las próximas 24 horas.",
  notFashionTitle: "Gracias por tu interés",
  notFashionSubtitle:
    "Trendo Boost trabaja solo con marcas de moda: ropa, calzado y accesorios. Si tu tienda entra en esa categoría, volvé a escribirnos con la web correcta.",
};

export const STATS: { value: string; label: string }[] = [
  { value: "Miles", label: "de usuarios mensuales" },
  { value: "+800K", label: "búsquedas realizadas" },
  { value: "+450", label: "marcas de moda" },
  { value: "+180K", label: "productos indexados" },
];

export const STATS_NOTE =
  "Miles de personas usan Trendo para descubrir marcas y encontrar qué comprar. Esos datos potencian las campañas de tu marca.";

export const PRODUCT_FLOW: {
  title: string;
  subtitle: string;
  steps: { icon: LucideIcon; title: string; description: string }[];
} = {
  title: "Trendo Boost corre tus campañas en Meta",
  subtitle:
    "Vos te enfocás en tu marca. Nosotros nos encargamos de que tus ads funcionen.",
  steps: [
    {
      icon: Images,
      title: "Publicás en Instagram",
      description:
        "Subís contenido como siempre. No tenés que cambiar nada de tu forma de trabajar.",
    },
    {
      icon: Sparkles,
      title: "Analizamos cada publicación",
      description:
        "Detectamos el contenido con potencial y lo incorporamos a la campaña.",
    },
    {
      icon: RefreshCw,
      title: "En piloto automático",
      description:
        "El contenido nuevo se agrega solo. Lo que deja de rendir se apaga.",
    },
  ],
};

export const AUTOMATION = {
  title: "¿Qué hace automáticamente Trendo Boost?",
  items: [
    "Configura y actualiza la campaña",
    "Incorpora nuevas publicaciones",
    "Detecta cuándo un anuncio deja de rendir",
    "Optimiza el presupuesto",
    "Analiza resultados",
    "Muestra recomendaciones",
  ],
};

export const PRODUCT_UI = {
  title: "Un panel simple para ver",
  titleSticker: "qué funciona",
  subtitle:
    "Seguí el rendimiento de tu contenido y tomá mejores decisiones sin entrar a Ads Manager todos los días.",
};

export const TESTIMONIALS: {
  quote: string;
  highlight: string;
  name: string;
  role: string;
  logoPath: string;
  logoWidth: number;
  logoHeight: number;
  link: string;
}[] = [
  {
    quote:
      "Gracias a Trendo pudimos optimizar nuestras campañas de publicidad. Nos permitió aumentar la visibilidad de la marca, conectar con nuevas clientas y generar un crecimiento significativo en nuestras ventas.",
    highlight: "generar un crecimiento significativo en nuestras ventas",
    name: "Camila Morales",
    role: "Fundadora de Amalfi",
    logoPath: "/logos-marcas/amalfi.webp",
    logoWidth: 1080,
    logoHeight: 1350,
    link: "https://amalfithebrand.com/",
  },
  {
    quote:
      "Desde que empezamos a trabajar con Trendo, notamos un cambio enorme. Nos ayudaron a darle más alcance a nuestra marca y eso se reflejó directamente en las ventas. Hoy es una herramienta clave para nuestro crecimiento.",
    highlight: "eso se reflejó directamente en las ventas",
    name: "Micaela Picardo",
    role: "Co-fundadora de Handbag",
    logoPath: "/logos-marcas/handbag.png",
    logoWidth: 1080,
    logoHeight: 1080,
    link: "https://handbag.com.uy/",
  },
  {
    quote:
      "Trabajar con Trendo Boost nos ayudó a aumentar el tráfico, mejorar la conversión y llegar a las personas correctas: las que conectan con nuestra propuesta. Se convirtió en un gran aliado para el crecimiento de nuestra marca.",
    highlight: "Se convirtió en un gran aliado",
    name: "Florencia De Mello",
    role: "Co-fundadora de Almira",
    logoPath: "/logos-marcas/almira.png",
    logoWidth: 2000,
    logoHeight: 2000,
    link: "https://almirauy.com/",
  },
];

export const PRICING = {
  badge: "Plan mensual",
  title: "Trendo Boost",
  currency: "USD",
  amount: "150",
  adSpendNote:
    "* El servicio de Trendo Boost no incluye la inversión en Meta Ads.",
};

export const FAQS: { question: string; answer: string }[] = [
  {
    question: "¿Funciona con mi plataforma de e-commerce?",
    answer:
      "Sí. Nos integramos con Meta, no con tu tienda, así que da igual si usás Shopify, Tiendanube, WooCommerce, Fenicio, Wix, Luna o una web hecha a medida. No tenés que migrar ni cambiar nada de tu tienda.",
  },
  {
    question: "¿Cuánto cuesta Trendo Boost?",
    answer: "Trendo Boost cuesta USD 150 por mes.",
  },
  {
    question: "¿El precio incluye la inversión en ads?",
    answer:
      "No. Los USD 150 por mes son por el servicio de Trendo Boost. La inversión publicitaria la definís vos, va directo a Meta y no pasa por nosotros. En la primera charla te ayudamos a definir con cuánto arrancar.",
  },
  {
    question: "¿Para qué tipo de marcas está pensado?",
    answer:
      "Trendo Boost está diseñado para marcas de moda de todos los tamaños. Desde pequeños emprendimientos, hasta marcas establecidas.",
  },
  {
    question: "¿Necesito estar invirtiendo en ads para usarlo?",
    answer:
      "No. Podemos ayudarte tanto si ya estás haciendo campañas como si todavía no empezaste.",
  },
  {
    question: "¿Qué beneficios puedo esperar?",
    answer:
      "Trendo Boost ayuda a las marcas a configurar mejor sus campañas, aprovechar mejor su presupuesto, entender qué contenido funciona mejor, detectar oportunidades usando datos reales de búsqueda, ahorrar tiempo en la gestión de ads y mejorar sus resultados online.",
  },
  {
    question: "¿Qué necesito para empezar?",
    answer:
      "Solo necesitás una tienda online y acceso a tu cuenta de Meta. El proceso de implementación es simple y lleva pocos minutos.",
  },
  {
    question: "¿Qué hace diferente a Trendo Boost?",
    answer:
      "Trendo Boost fue diseñado específicamente para marcas de moda. Además de ayudarte a configurar y optimizar campañas, utiliza datos reales de búsqueda dentro de trendo.uy para detectar oportunidades que otras plataformas no pueden ver.",
  },
  {
    question: "¿Tengo que entender de Meta Ads?",
    answer:
      "No. La idea es justamente simplificar una de las partes más complejas de vender online. Nosotros nos ocupamos de la configuración y las recomendaciones para que puedas enfocarte en hacer crecer tu marca.",
  },
];
